/**
 * Deleting an ordre, out of the way.
 *
 * Two calls, one of them through the garage's proxy (the ServiceBox RDV), which queues
 * behind whatever else that account is doing and can take a while. Holding the modal open
 * on a spinner for that served nobody: the decision is made, the work is not the user's to
 * watch. It runs here instead, and says where it is in the top-right task board.
 */
import { ordresApi } from "@services/api/ordres";
import { serviceboxProxy, type ServiceBoxCredentials } from "@services/serviceboxProxy";
import { setBackgroundTask } from "@services/backgroundTasks";
import { showNotification } from "context/notification.service";
import { usePlanningStore } from "@store/planningStore";
import { useAtelierStore } from "@store/atelierStore";
import type { Ordre } from "types/ordre";

export interface DeleteOrdreParams {
  ordre: Ordre;
  /** Also cancel the linked RDV in ServiceBox — best effort, never blocks the deletion. */
  deleteInServiceBox: boolean;
  sbCreds?: ServiceBoxCredentials;
}

/** Fire-and-forget: never rejects. The outcome is a task pill plus a toast. */
export async function deleteOrdreInBackground({ ordre, deleteInServiceBox, sbCreds }: DeleteOrdreParams): Promise<void> {
  const taskId = `delete:${ordre.id}`;
  const label = `Suppression ${ordre.num || ordre.immatriculation || "de l'ordre"}`;
  setBackgroundTask(taskId, { label, status: "running", detail: "Suppression en cours…" });

  if (deleteInServiceBox && ordre.servicebox_rdv_id && sbCreds) {
    setBackgroundTask(taskId, { status: "running", detail: "Annulation du RDV ServiceBox…" });
    try {
      // onBusy fires on every 423 retry (every 2 s): the task pill is the place for that,
      // a toast per retry per ordre floods the screen on a bulk deletion.
      await serviceboxProxy.deleteRdv(sbCreds, ordre.servicebox_rdv_id, () => {
        setBackgroundTask(taskId, { status: "waiting", detail: "En attente du proxy ServiceBox…" });
      });
    } catch (e: any) {
      // The ordre still goes: ServiceBox refusing is not a reason to keep a deleted RDV in Carvis.
      showNotification({ type: "error", message: `Suppression ServiceBox échouée : ${e?.message || "erreur"}` });
    }
  }

  try {
    setBackgroundTask(taskId, { status: "running", detail: "Suppression de l'ordre…" });
    await ordresApi.delete(ordre.id);
  } catch {
    setBackgroundTask(taskId, { status: "error", detail: "l'ordre n'a pas été supprimé" });
    showNotification({ type: "error", message: "Erreur lors de la suppression" });
    return;
  }

  const planningState = usePlanningStore.getState();
  if (planningState._fetchedOrgId) planningState.fetchAppointments(planningState._fetchedOrgId, true);
  const atelierState = useAtelierStore.getState();
  if (atelierState._fetchedOrgId) atelierState.fetchData(atelierState._fetchedOrgId, true);

  setBackgroundTask(taskId, { status: "done", detail: "Ordre supprimé" });
  showNotification({ type: "success", message: "Ordre supprimé" });
}
